import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Hidden } from '@mui/material';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import Banner from '@cpg-its/ads.containers.banner';
import { Redirect } from 'react-router';
import { TcsmAppConfigs } from '../../../global/models/configuration-models';
import { getTcsmEpiProps, setPodsSettings } from '../../../global/ducks/epi-api';
import { TcsmStates } from '../../../root/tcsm-model';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { MyCpgPodsSettings, MyCpgPodsState } from '../mycpg-pod-models';
import { PageContainer } from '../../../global/components/layout/page-container';
import { MainContent } from '../../../global/components/layout/main-content-container';
import { PageHeader } from '../../../global/components/layout/page-header';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { SideBarContent } from '../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../global/components/guided-process-support-block';
import { GpsType } from '../../../global/components/layout/layout-models';
import { useGpsType } from '../../../global/hooks/useGpsType';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { ExternalLink } from '../../../global/components/external-link-modal';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { getMyCpgPodInfo, getGhlBillingInfoByGuid, getPensionBillingInfoByGuid } from '../../../global/ducks/mycpg-pod-api';
import { setPods, setPodsError, setGhlBillingInfo, setGhlBillingInfoError, setPensionBillingInfo, setPensionBillingInfoError } from '../mycpg-pod-slice';
import { YesNoIndicators } from '../../../global/models/api-model';
import { AccountInfoRoutes } from '../../account-info/constants/routes';
import { useBasePath } from '../../../global/hooks/useBasePath';
import { stripHtmlAndCleanText, stripWrappingNbsp } from '../../../global/helpers/html-to-string-helpers';

interface Props {
    appConfig: TcsmAppConfigs;
}

export function MyCpgBilling(props: Props) {

    const { appConfig } = props;
    const dispatch = useDispatch();

    const guid = useSelector<TcsmStates, string>(state => state.authenticatedState?.clientGuid);
    const language = useSelector<TcsmStates, string>(state => state.globalization?.currentlanguage);
    const settings = useSelector<TcsmStates, MyCpgPodsSettings>(state => state.epiProps?.podsSettingsState?.epiContent);
    const pods = useSelector<TcsmStates, MyCpgPodsState>(state => state?.mycpgPodsState);
    const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);
    const gpsItem = useGpsType(GpsType.MYCPG_BILLING);
    const basePath = useBasePath(AccountInfoRoutes.MyCpgRoot);
    const ghlInfo = pods?.ghlInfo;
    const pensionInfo = pods?.pensionInfo;
    const myCpgBillPayNotificationLabel = stripHtmlAndCleanText(labelSettings?.myCpgBillPayNotificationLabel);
    const myCpgBillPayNotificationDescription = stripWrappingNbsp(labelSettings?.myCpgBillPayNotificationDescription);

    useEffect(() => {
        if (appConfig?.podsSettingsId) {
            dispatch(getTcsmEpiProps(dispatch, appConfig?.podsSettingsId, { guid }, language, false, setPodsSettings));
        }
    }, [appConfig]);

    useEffect(() => {
        if (pods.podsLoaded) {
            dispatch(getMyCpgPodInfo(
                (data) => {
                    dispatch(setPods(data?.data));
                },
                () => {
                    dispatch(setPodsError());
                },
            ));
        }
    }, []);

    useEffect(() => {
      if (!pods.ghlInfoLoaded || !pods.pensionInfoLoaded) {
        dispatch(getGhlBillingInfoByGuid(
          guid,
          (data) => {
            dispatch(setGhlBillingInfo(data?.data));
          },
          () => {
            dispatch(setGhlBillingInfoError());
          },
        ));
        dispatch(getPensionBillingInfoByGuid(
          guid,
          (data) => {
            dispatch(setPensionBillingInfo(data?.data));
          },
          () => {
            dispatch(setPensionBillingInfoError());
          },
        ));
      }
    }, [guid]);

    if (pods?.pods?.ghl_bill_payer !== YesNoIndicators.Yes && pods.pods?.pension_bill_payer !== YesNoIndicators.Yes) {
        return <Redirect to={basePath} />;
    }

    if (!settings || !pods.podsLoaded) {
        return <LoadingCircle />;
    }

    const renderBillInfo = (plan) => (
      <>
        {plan.plan_name && (
          <Grid item xs={12} md={12} lg={12} style={{ marginBottom: '.5rem' }}>
            <p>{plan.plan_name}</p>
          </Grid>
        )}
          <Grid container>
            <Grid item xs={4} md={4} lg={4}>
              <h4>{settings.acctNumHeader}</h4>
            </Grid>
            <Grid item xs={8} md={8} lg={8}>
              <p>{plan.account_number}</p>
            </Grid>
            {plan.billing_account_name && (
              <>
                <Grid item xs={4} md={4} lg={4}>
                  <h4>{settings.acctNameHeader}</h4>
                </Grid>
                <Grid item xs={8} md={8} lg={8}>
                  <p>{plan.billing_account_name}</p>
                </Grid>
              </>
            )}
          </Grid>
      </>
    );

  return (
    <PageContainer>
      <MainContent>
        <ContentSpacer>
          <Banner
            banner={{
              label: myCpgBillPayNotificationLabel,
              description: <WysiwygWithReactLink content={myCpgBillPayNotificationDescription} />,
              type: 'info',
              removable: true,
            }}
          />
        </ContentSpacer>
        <PageHeader headerText={settings.mycpgBillingTitle} description={null} />
        <Hidden smUp>
        <SideBarContent>
          <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
        </SideBarContent>
        </Hidden>
        <ContentSpacer>
          <p>{settings.billingPageInstructions}</p>
        </ContentSpacer>
        <ContentSpacer>
          <ExternalLink className="btn-primary" linkUrl={labelSettings?.paymentusUrl} linkText={labelSettings?.paymentusButtonText} />
        </ContentSpacer>

            {
              pods?.pods?.pension_bill_payer === YesNoIndicators.Yes && pensionInfo.length > 0 && (
                !pods.pensionInfoLoaded ? (
                  <LoadingCircle />
                ) : (
                  <DividerSection>
                    <h2 style={{ marginBottom: '1rem' }}>Pension</h2>
                    {pensionInfo.map(pensionPlan => renderBillInfo(pensionPlan))}
                  </DividerSection>
                )
              )
            }
            {
              pods?.pods?.ghl_bill_payer === YesNoIndicators.Yes && ghlInfo.length > 0 && (
                !pods.ghlInfoLoaded ? (
                  <LoadingCircle />
                ) : (
                  <DividerSection>
                      <h2 style={{ marginBottom: '1rem' }}>Health</h2>
                      {ghlInfo.map(ghlPlan => renderBillInfo(ghlPlan))}
                  </DividerSection>
                )
              )
            }

        <div>
            <button className='link-button' type='button' onClick={() => window.open(settings?.medicalTrustLink, '_blank')}>Medical Trust Disclaimer</button>
        </div>
        <div>
            <button className='link-button' type='button' onClick={() => window.open(settings?.hippaNoticeLink, '_blank')}>HIPPA Privacy Notice</button>
        </div>
      </MainContent>
      <Hidden smDown>
    <SideBarContent>
      <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
    </SideBarContent>
    </Hidden>
    </PageContainer>
  );
}

